import { useState, useEffect, useCallback, useRef } from 'react';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function Nominas() {
  const user = useAuthStore(s => s.user);
  if (!ROLES_ADMIN.includes(user?.rol)) {
    return (
      <div style={{ padding: 32, color: 'var(--text-dim)', textAlign: 'center', fontSize: 13 }}>
        Esta sección es solo para administradores.<br />
        Tus nóminas están disponibles en <b>Mi Perfil</b>.
      </div>
    );
  }
  return <NominasAdmin user={user} />;
}

function NominasAdmin({ user }) {
  const [employees, setEmployees] = useState([]);
  const [nominas,   setNominas]   = useState([]);
  const [loading,   setLoading]   = useState(true);

  const thisYear = new Date().getFullYear();
  const [selEmp,    setSelEmp]    = useState('');
  const [sedeFiltro,setSedeFiltro]= useState('sevilla');
  const [selMes,    setSelMes]    = useState(new Date().getMonth() + 1);
  const [selAño,    setSelAño]    = useState(thisYear);
  const [pdfFile,   setPdfFile]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, nomRes] = await Promise.all([
        api.get('/api/employees/list').catch(() => ({ data: [] })),
        api.get('/api/nominas/all').catch(() => ({ data: [] })),
      ]);
      setEmployees(empRes.data || []);
      setNominas(nomRes.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setPdfFile({ b64: ev.target.result.split(',')[1], name: file.name });
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  async function uploadNomina() {
    if (!selEmp || !pdfFile) { alert('Selecciona empleado y PDF'); return; }
    setUploading(true);
    try {
      const r = await api.post('/api/nominas/create', {
        user_id:   parseInt(selEmp),
        mes:       selMes,
        año:       selAño,
        file_b64:  pdfFile.b64,
        file_name: pdfFile.name,
      });
      const emp = employees.find(e => String(e.user_id) === String(selEmp));
      setNominas(prev => {
        const filtered = prev.filter(n =>
          !(n.user_id === r.data.user_id && n.mes === r.data.mes && n.año === r.data.año));
        return [{ ...r.data, user_nombre: emp?.nombre || '' }, ...filtered]
          .sort((a, b) => b.año - a.año || b.mes - a.mes || a.user_nombre?.localeCompare(b.user_nombre));
      });
      setPdfFile(null);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setUploading(false); }
  }

  async function deleteNomina(id) {
    if (!window.confirm('¿Eliminar esta nómina?')) return;
    try {
      await api.delete(`/api/nominas/${id}`);
      setNominas(prev => prev.filter(n => n.id !== id));
    } catch (e) { alert(e.message); }
  }

  async function downloadNomina(n) {
    try {
      const blob = await api.get(`/api/nominas/${n.id}/file`, { responseType: 'blob' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = n.file_name || `nomina-${MESES[(n.mes||1)-1]}-${n.año}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert('No se pudo descargar el archivo'); }
  }

  if (loading) return <div style={{ padding: 32, color: 'var(--text-dim)', textAlign: 'center' }}>Cargando…</div>;

  const linkedEmps = employees.filter(e => e.user_id && (e.sede || 'sevilla') === sedeFiltro);
  const years      = [thisYear - 1, thisYear, thisYear + 1];

  return (
    <div style={{ padding: 'clamp(12px,3vw,24px)', maxWidth: 860, margin: '0 auto' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', marginBottom: 18 }}>Nóminas</div>

      {/* Upload form */}
      <div style={CARD}>
        <div style={STITLE}>📤 Subir nómina</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10, marginBottom: 4 }}>
          {['sevilla','granada'].map(s => (
            <button key={s} onClick={() => { setSedeFiltro(s); setSelEmp(''); }} style={{
              padding: '4px 14px', borderRadius: 6, fontWeight: 800, fontSize: 11, cursor: 'pointer',
              textTransform: 'capitalize',
              background: sedeFiltro === s ? 'var(--amber)' : 'var(--surface2)',
              color: sedeFiltro === s ? '#000' : 'var(--text-dim)',
              border: sedeFiltro === s ? 'none' : '1px solid var(--border)',
            }}>{s.charAt(0).toUpperCase()+s.slice(1)}</button>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px,1fr))',
            gap: 10, marginTop: 8 }}>
          <Fld label="Empleado">
            <select value={selEmp} onChange={e => setSelEmp(e.target.value)} style={SEL}>
              <option value="">Seleccionar…</option>
              {linkedEmps.map(e => (
                <option key={e.user_id} value={e.user_id}>{e.nombre}</option>
              ))}
            </select>
          </Fld>
          <Fld label="Mes">
            <select value={selMes} onChange={e => setSelMes(parseInt(e.target.value))} style={SEL}>
              {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </Fld>
          <Fld label="Año">
            <select value={selAño} onChange={e => setSelAño(parseInt(e.target.value))} style={SEL}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </Fld>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => fileRef.current?.click()}
            style={{ background: '#0e1f3d', border: '1px solid #1a3560', color: '#60a5fa',
              padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
            {pdfFile ? '✓ ' + pdfFile.name.slice(0, 32) : '📎 Seleccionar PDF'}
          </button>
          {pdfFile && (
            <button onClick={() => setPdfFile(null)}
              style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 18 }}>
              ✕
            </button>
          )}
          <input ref={fileRef} type="file" accept="application/pdf,.pdf"
            style={{ display: 'none' }} onChange={handleFile} />

          <button onClick={uploadNomina} disabled={!selEmp || !pdfFile || uploading}
            style={{ background: (!selEmp || !pdfFile) ? '#555' : 'var(--amber)', color: '#000',
              border: 'none', padding: '8px 18px', borderRadius: 7, fontWeight: 800, fontSize: 12,
              cursor: (!selEmp || !pdfFile || uploading) ? 'not-allowed' : 'pointer',
              opacity: (!selEmp || !pdfFile) ? 0.5 : 1 }}>
            {uploading ? 'Subiendo…' : '⬆ Subir'}
          </button>
        </div>
      </div>

      {/* List */}
      <div style={{ ...CARD, marginTop: 14 }}>
        <div style={STITLE}>📋 Nóminas subidas ({nominas.length})</div>
        {nominas.length === 0 ? (
          <div style={{ color: 'var(--text-dim)', fontSize: 12, marginTop: 10 }}>
            Sin nóminas subidas aún.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
            {nominas.map(n => (
              <div key={n.id} style={ROW}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                    {n.user_nombre}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                    {MESES[(n.mes || 1) - 1]} {n.año}
                    {' · '}
                    {n.leida
                      ? <span style={{ color: '#4ade80' }}>Descargada ✓</span>
                      : <span style={{ color: '#fbbf24' }}>Pendiente de leer</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => downloadNomina(n)} style={BTN_DL} title="Descargar PDF">
                    ⬇ PDF
                  </button>
                  <button onClick={() => deleteNomina(n.id)} style={BTN_DEL} title="Eliminar">
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 10, color: '#4e5a7a', fontWeight: 700, letterSpacing: '.05em',
        textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

const CARD   = { background: '#10131e', border: '1px solid #1c2035', borderRadius: 10, padding: '14px 16px' };
const STITLE = { fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' };
const SEL    = { background: '#070910', border: '1.5px solid #1c2035', borderRadius: 7,
  color: 'var(--text)', padding: '8px 10px', fontSize: 12, fontFamily: 'inherit', width: '100%' };
const ROW    = { background: '#070910', border: '1px solid #1c2035', borderRadius: 7,
  padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 12 };
const BTN_DL  = { background: '#0d2010', border: '1px solid #166534', color: '#4ade80',
  borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700 };
const BTN_DEL = { background: '#1a0a0a', border: '1px solid #7f1d1d', color: '#f87171',
  borderRadius: 6, padding: '5px 8px', cursor: 'pointer', fontSize: 13 };
